import { Destination } from '@m/state/destinations/types';
import getCoordinateDistance from '@m/utils/getCoordinateDistance';

/** Mapbox types suitable for parking location search (excludes region/country). */
export const SEARCH_GEOCODE_TYPES = [
  'place',
  'locality',
  'neighborhood',
  'address',
  'poi',
] as const;

/** Lower rank = more specific (preferred when labels or centers collide). */
const PLACE_TYPE_SPECIFICITY: Record<string, number> = {
  address: 0,
  poi: 1,
  neighborhood: 2,
  locality: 3,
  place: 4,
};

/** ~110m — collapses near-duplicate Mapbox centers regardless of grid boundaries. */
const NEAR_DUPLICATE_MILES = 0.07;

function placeTypeSpecificity(placeType: string[] | undefined): number {
  const primary = placeType?.[0];
  if (primary && primary in PLACE_TYPE_SPECIFICITY) {
    return PLACE_TYPE_SPECIFICITY[primary];
  }
  return 50;
}

function centersNearDuplicate(a: [number, number], b: [number, number]): boolean {
  return getCoordinateDistance(a, b) <= NEAR_DUPLICATE_MILES;
}

function isMoreSpecific(a: Destination, b: Destination): boolean {
  return placeTypeSpecificity(a.place_type) < placeTypeSpecificity(b.place_type);
}

/**
 * Drop duplicate geocode features that share a place name or near-identical center,
 * keeping the more specific `place_type` when they collide. A single incoming feature
 * can collide with multiple existing rows (name vs center); those are merged into one.
 */
export function dedupeSearchDestinations(features: Destination[]): Destination[] {
  const result: Destination[] = [];

  for (const feature of features) {
    const nameKey = feature.place_name.trim().toLowerCase();
    const collideIdxs: number[] = [];
    result.forEach((existing, idx) => {
      if (
        existing.place_name.trim().toLowerCase() === nameKey ||
        centersNearDuplicate(existing.center, feature.center)
      ) {
        collideIdxs.push(idx);
      }
    });

    if (collideIdxs.length === 0) {
      result.push(feature);
      continue;
    }

    let winner: Destination = feature;
    for (const idx of collideIdxs) {
      const existing = result[idx];
      if (!isMoreSpecific(winner, existing)) {
        winner = existing;
      }
    }

    // Keep the winner at the earliest collided slot so Enter/Search still
    // commits Mapbox's top-ranked result (`destinations[0]`), not a later row
    // that only became first because a merge appended the winner.
    const insertAt = Math.min(...collideIdxs);
    for (const idx of [...collideIdxs].sort((a, b) => b - a)) {
      result.splice(idx, 1);
    }
    result.splice(insertAt, 0, winner);
  }

  return result;
}
